'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Trophy } from 'lucide-react';
import { ApexLegendIcon } from '@/components/ApexLegendIcon';

const CACHE_TTL_MS = 5 * 60 * 1000;
const apexCache = new Map<string, { expiresAt: number; data: ApexStatsData }>();
const apexInflight = new Map<string, Promise<ApexStatsData>>();

interface ApexRank {
  rankScore: number;
  rankName: string;
  rankDiv: number;
  rankImg: string;
}

interface ApexLegendStat {
  name: string;
  value: number;
  key: string;
}

interface ApexStatsData {
  global: {
    name: string;
    uid: string;
    platform: string;
    level: number;
    toNextLevelPercent: number;
    levelPrestige: number;
    rank: ApexRank;
  };
  realtime: {
    isOnline: number;
    selectedLegend: string;
    currentStateAsText: string;
  };
  legends: {
    selected: {
      LegendName: string;
      data?: ApexLegendStat[];
      ImgAssets?: { icon: string; banner: string };
    };
  };
  total?: Record<string, { name: string; value: number }>;
}

interface ApexStatsProps {
  playerId: string;
  platform: string; // X1 | PS4 | PC
  gamertag?: string;
  locale?: string;
  /** Rendered under the career stats, inside the card (tournament history). */
  footer?: React.ReactNode;
}

/** Soft glow behind the rank badge, tinted by tier. The card itself stays neutral. */
const rankTierGlow = (rankName: string): string => {
  const key = (rankName || '').toLowerCase();
  if (key.includes('predator')) return 'bg-red-500/30';
  if (key.includes('master')) return 'bg-purple-500/30';
  if (key.includes('diamond')) return 'bg-blue-400/30';
  if (key.includes('platinum')) return 'bg-cyan-400/25';
  if (key.includes('gold')) return 'bg-yellow-500/25';
  if (key.includes('silver')) return 'bg-gray-300/20';
  if (key.includes('bronze')) return 'bg-orange-600/25';
  return 'bg-white/10';
};

const PLATFORM_LABEL: Record<string, string> = { PC: 'PC', PS4: 'PlayStation', X1: 'Xbox', SWITCH: 'Switch' };

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

export default function ApexStats({ playerId, platform, gamertag, locale = 'en', footer }: ApexStatsProps) {
  const leaderboardHref = gamertag
    ? `/${locale}/leaderboard/apex?player=${encodeURIComponent(gamertag)}`
    : `/${locale}/leaderboard/apex`;
  const [stats, setStats] = useState<ApexStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!playerId || !platform) {
        setError('Missing player or platform');
        setLoading(false);
        return;
      }

      const key = `${playerId}|${platform}`;
      const cached = apexCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        setStats(cached.data);
        setLoading(false);
        return;
      }

      let pending = apexInflight.get(key);
      if (!pending) {
        pending = (async () => {
          const response = await fetch(
            `/api/apex-stats?player=${encodeURIComponent(playerId)}&platform=${encodeURIComponent(platform)}`
          );
          if (!response.ok) {
            const err = new Error(response.status === 404 ? 'Player not found' : 'Failed to load stats');
            (err as Error & { status?: number }).status = response.status;
            throw err;
          }
          const data: ApexStatsData = await response.json();
          apexCache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, data });
          return data;
        })().finally(() => {
          apexInflight.delete(key);
        });
        apexInflight.set(key, pending);
      }

      try {
        setLoading(true);
        setError(null);
        const data = await pending;
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [playerId, platform]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-card/60 p-5" aria-busy="true">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 animate-pulse rounded-xl bg-white/10" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
            <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
          </div>
        </div>
        <div className="mt-5 h-20 animate-pulse rounded-xl bg-white/[0.04]" />
        <div className="mt-3 h-16 animate-pulse rounded-xl bg-white/[0.04]" />
      </div>
    );
  }

  if (error || !stats) {
    // Stats failed: still show what sits under them (tournament history).
    return footer ? <div className="rounded-2xl border border-white/10 bg-card/60 p-5">{footer}</div> : null;
  }

  const { global, realtime, legends, total } = stats;
  const selectedLegend = realtime.selectedLegend || legends.selected.LegendName;
  const legendIcon = legends.selected.ImgAssets?.icon;

  const pickCareerStat = (keys: string[]) => {
    if (!total) return null;
    for (const key of keys) {
      const entry = total[key];
      if (entry && typeof entry.value === 'number') return entry;
    }
    return null;
  };

  const kills = pickCareerStat(['kills', 'specialEvent_kills']);
  const wins = pickCareerStat(['wins', 'specialEvent_wins']);
  const damage = pickCareerStat(['damage', 'specialEvent_damage']);

  const tiles: { label: string; value: number; sub?: string }[] = [
    { label: 'Level', value: global.level, sub: global.levelPrestige > 0 ? `Prestige ${global.levelPrestige}` : undefined },
    ...(kills ? [{ label: 'Kills', value: kills.value }] : []),
    ...(wins ? [{ label: 'Wins', value: wins.value }] : []),
    ...(damage ? [{ label: 'Damage', value: damage.value }] : []),
  ];
  const platformLabel = PLATFORM_LABEL[platform] ?? platform;

  return (
    <div className="flex flex-col rounded-2xl border border-white/10 bg-card/60 p-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-secondary ring-1 ring-white/10">
          <ApexLegendIcon
            url={legendIcon}
            legend={selectedLegend}
            alt={selectedLegend || 'Apex Legend'}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-white">Apex Legends</p>
          <p className="truncate text-sm text-muted-foreground">
            {platformLabel}
            {selectedLegend ? ` · Maining ${selectedLegend}` : ''}
          </p>
        </div>
        {realtime.isOnline ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />
            Online
          </span>
        ) : null}
      </div>

      {/* BR Rank */}
      {global.rank?.rankName ? (
        <div className="mt-5 flex items-center gap-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-white/[0.06]">
          <div className="relative h-14 w-14 shrink-0">
            <div className={`absolute inset-1 rounded-full blur-xl ${rankTierGlow(global.rank.rankName)}`} aria-hidden />
            {global.rank.rankImg ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={global.rank.rankImg} alt="" className="relative h-full w-full object-contain" />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">BR Rank</p>
            <p className="mt-0.5 truncate text-xl font-bold leading-tight text-white">
              {global.rank.rankName}
              {global.rank.rankDiv ? ` ${global.rank.rankDiv}` : ''}
            </p>
            <p className="text-sm tabular-nums text-white/55">{global.rank.rankScore.toLocaleString()} RP</p>
          </div>
          <Link
            href={leaderboardHref}
            aria-label="Leaderboard"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Trophy size={14} aria-hidden />
            <span className="hidden sm:inline">Leaderboard</span>
          </Link>
        </div>
      ) : null}

      {/* Career stats */}
      <dl className="mt-3 grid grid-cols-2 overflow-hidden rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] sm:grid-cols-4">
        {tiles.map((t, i) => (
          <div
            key={t.label}
            className={`p-4 ${i % 2 === 1 ? 'border-l border-white/[0.06]' : ''} ${i >= 2 ? 'border-t border-white/[0.06] sm:border-t-0' : ''} ${i === 2 ? 'sm:border-l' : ''}`}
          >
            <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t.label}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-white" title={t.value.toLocaleString()}>
              {t.value >= 100_000 ? compact.format(t.value) : t.value.toLocaleString()}
            </dd>
            {t.sub ? <dd className="text-xs text-white/45">{t.sub}</dd> : null}
          </div>
        ))}
      </dl>

      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}
